import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {MixRgbSockets} from "#material/nodes/color/mix-rgb/definition"
import {mixRgbDefinition} from "#material/nodes/color/mix-rgb/definition"
import {cyclesMixType} from "#material/nodes/color/mix-rgb/helpers"
import type {RgbBlendType} from "#material/nodes/color/mix-rgb/validation"
import type {CyclesReturns} from "#material/sockets/cycles"
import {getColor, threeConvert, threeRGBColor, threeValue} from "#material/three/three-utils"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import {cyclesColor} from "#material/cycles/helpers"
import type {ThreeReturns} from "#material/sockets/three"
import type {MixNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class MixRgb extends MaterialNode<MixRgbSockets> {
    public override get definition() {
        return mixRgbDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<MixRgbSockets>> => {
        const {color1, color2, fac} = await evaluator.getAll({
            color1: this.parameters.inputs.color1,
            color2: this.parameters.inputs.color2,
            fac: this.parameters.inputs.fac,
        })
        const inputA =
            color1?.toColor() ??
            threeConvert(this.parameters.settings.color1, threeRGBColor) ??
            threeRGBColor({
                r: 0,
                g: 0,
                b: 0,
            })
        const inputB =
            color2?.toColor() ??
            threeConvert(this.parameters.settings.color2, threeRGBColor) ??
            threeRGBColor({
                r: 0,
                g: 0,
                b: 0,
            })
        const facNode = fac?.toFloat() ?? threeConvert(this.parameters.settings.fac, threeValue) ?? threeValue(1)
        const {blendType: blendType, useClamp} = this.parameters.settings
        const operation = (blendType ?? "MIX") as RgbBlendType
        const mixedColor = getColor(operation, inputA, inputB, facNode)
        return {color: useClamp ? TSL.color(TSL.clamp(mixedColor.rgb, threeValue(0), threeValue(1))) : mixedColor}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<MixRgbSockets>> => {
        const mixNode: MixNode = {
            type: "mix",
            inputs: await evaluator.getAll({
                color1: this.parameters.inputs.color1,
                color2: this.parameters.inputs.color2,
                fac: this.parameters.inputs.fac,
            }),
            parameters: {
                color1: cyclesColor(this.parameters.settings.color1),
                color2: cyclesColor(this.parameters.settings.color2),
                mix_type: cyclesMixType(this.parameters.settings.blendType),
                fac: this.parameters.settings.fac,
                use_clamp: this.parameters.settings.useClamp,
            },
            resources: {},
        }

        return {color: [mixNode, "color"]}
    }
}
